import axios from "axios";
import { fetchGet, fetchPost } from "../api_client";

export const FILE_CHUNK_SIZE = 10000000;

export const GetUploadUrlsApi = async ({
  mimetype,
  parts,
  belong,
}: {
  belong: string;
  mimetype: string;
  parts: number;
}) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/get-upload-url?mimetype=${mimetype}&parts=${parts}&belong=${belong}`;
    const res = await fetchGet(url);

    return res;
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Sends one chunk to the API, which performs the S3 UploadPart itself.
 *
 * The browser cannot PUT to the presigned URLs from GetUploadUrlsApi: the IAM
 * user signing them holds PutObject only under request context a browser cannot
 * reproduce, so S3 answers AccessDenied.
 */
export const UploadPartApi = async ({
  chunk,
  key,
  upload_id,
  part_number,
  onProgress,
}: {
  chunk: Blob | ArrayBuffer;
  key: string;
  upload_id: string;
  part_number: number;
  onProgress?: (event: any) => void;
}) => {
  const token = localStorage.getItem("token");
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/upload-part`;

  const res = await axios.post(url, chunk, {
    withCredentials: true,
    headers: {
      "Content-Type": "application/octet-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      key,
      upload_id,
      part_number: `${part_number}`,
    },
    onUploadProgress: onProgress,
  });

  return res.data;
};

export const CompleteUploadApi = async (data: {
  mimetype: string;
  meta: {
    belong: string;
    /** Omitted when the owning record does not exist yet — the API then just
     * returns the uploaded object's URL instead of attaching it. */
    ref_id?: string;
    category?: string;
    name?: string;
  };
  key: string;
  upload_id: string;
}) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/complete-upload`;
    const res = await fetchPost(url, data);

    return res;
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
export const CancelUploadApi = async (data: {
  key: string;
  upload_id: string;
}) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/cancel-upload`;
    const res = await fetchPost(url, data);

    return res;
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Uploads a file to S3 in chunks, routed through the API rather than direct from
 * the browser — see UploadPartApi for why direct is not possible. Only one chunk
 * occupies a request body at a time, so no proxy or API process ever buffers the
 * whole file, which is the property the presigned path was adopted for.
 *
 * Pass `ref_id` to have the API attach the object to that record on completion.
 * Omit it — as complaint creation does, having no complaint id yet — and the
 * object's `url` comes back for the caller to submit later.
 *
 * Chunks are read with Blob.slice rather than reading the whole file into an
 * ArrayBuffer per part, which keeps memory flat for large videos.
 */
export const uploadFileViaBackend = async ({
  file,
  belong,
  ref_id,
  category,
  onProgress,
}: {
  file: File;
  belong: string;
  ref_id?: string;
  category?: string;
  onProgress?: (percent: number) => void;
}): Promise<{ success: boolean; url?: string; error?: string }> => {
  const parts = Math.max(1, Math.ceil(file.size / FILE_CHUNK_SIZE));

  const init = await GetUploadUrlsApi({
    mimetype: file.type,
    parts,
    belong,
  });

  if (!init.success)
    return {
      success: false,
      error: init.error ?? "Could not start the upload. Please retry.",
    };

  const { key, upload_id } = init.data;

  try {
    for (let index = 0; index < parts; index++) {
      const chunk = file.slice(
        index * FILE_CHUNK_SIZE,
        Math.min((index + 1) * FILE_CHUNK_SIZE, file.size),
      );

      const part = await UploadPartApi({
        chunk,
        key,
        upload_id,
        part_number: index + 1,
        onProgress: (event: any) => {
          if (!onProgress) return;
          const withinPart = event.total ? event.loaded / event.total : 0;
          onProgress(Math.min(100, ((index + withinPart) / parts) * 100));
        },
      });

      // A handled failure comes back as 200 with `{ success: false }`, so a
      // rejected promise is not the only way a part can fail.
      if (!part?.success)
        throw new Error(
          part?.error ?? `Chunk ${index + 1} of ${parts} failed.`,
        );
    }
  } catch (error: any) {
    await CancelUploadApi({ key, upload_id });
    return {
      success: false,
      error: error?.message ?? "Upload failed part way through. Please retry.",
    };
  }

  const completed = await CompleteUploadApi({
    upload_id,
    key,
    mimetype: file.type,
    meta: { belong, ref_id, category },
  });

  if (!completed.success)
    return {
      success: false,
      error: completed.error ?? "Could not finalise the upload.",
    };

  onProgress?.(100);
  return { success: true, url: completed.data?.url };
};
